import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import styled from 'styled-components';

import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Drawer } from '@sorare/core/src/atoms/layout/Drawer';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { SBHorizontal } from '@sorare/core/src/atoms/layout/flex';
import { DialogKey } from '@sorare/core/src/components/navigation/WithDialogs';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { useOpenDialog } from '@sorare/core/src/hooks/navigation/useOpenDialog';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';

import { LeaderboardRewards } from 'components/leaderboard/LeaderboardRewards';

import { So5LeaderboardHeader } from '../So5LeaderboardHeader';
import type {
  LazyPrizePoolDrawerQuery,
  LazyPrizePoolDrawerQueryVariables,
} from './__generated__/index.graphql';

const DrawerContent = styled(FullWidthContainer)`
  width: 100vw;
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: var(--triple-unit);
  background: var(--c-black);
  padding-top: var(--double-unit);
  padding-bottom: var(--double-unit);

  @media ${laptopAndAbove} {
    width: 420px;
  }
`;

const LAZY_PRIZE_POOL_DRAWER_QUERY = gql`
  query LazyPrizePoolDrawerQuery($so5LeaderboardSlug: String!) {
    so5 {
      so5Leaderboard(slug: $so5LeaderboardSlug) {
        slug
        so5LineupsCount
        ...LeaderboardRewards_so5Leaderboard
        ...So5LeaderboardHeader_so5Leaderboard
      }
    }
  }
  ${LeaderboardRewards.fragments.so5Leaderboard}
  ${So5LeaderboardHeader.fragments.so5Leaderboard}
` as TypedDocumentNode<
  LazyPrizePoolDrawerQuery,
  LazyPrizePoolDrawerQueryVariables
>;

type Props = {
  so5LeaderboardSlug: Nullable<string>;
  open: boolean;
  onClose: () => void;
};

export const LazyPrizePoolDrawer = ({
  so5LeaderboardSlug,
  open,
  onClose,
}: Props) => {
  const openDialog = useOpenDialog();
  const { data } = useQuery(LAZY_PRIZE_POOL_DRAWER_QUERY, {
    variables: { so5LeaderboardSlug: so5LeaderboardSlug || '' },
    skip: !so5LeaderboardSlug,
  });

  const { so5Leaderboard } = data?.so5 || {};

  if (!so5Leaderboard) {
    return null;
  }

  return (
    <Drawer open={open} onBackdropClick={onClose}>
      <DrawerContent>
        <SBHorizontal>
          <So5LeaderboardHeader so5Leaderboard={so5Leaderboard} />
          <IconButton icon={faTimes} color="transparent" onClick={onClose} />
        </SBHorizontal>
        <LeaderboardRewards
          so5Leaderboard={so5Leaderboard}
          onClickRanking={value => openDialog(DialogKey.contender, value)}
        />
      </DrawerContent>
    </Drawer>
  );
};
